import { useState, useEffect } from 'react';

export interface AccessibilitySettings {
  fontSize: 'normal' | 'large' | 'xlarge';
  largeCursor: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'normal',
  largeCursor: false,
};

const STORAGE_KEY = 'accessibility_settings';

export function useAccessibility() {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  // Apply on mount
  useEffect(() => {
    applySettings(settings);
  }, []);

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement;

    // Font size — aplica no <html> para escalar todos os rem
    root.classList.remove('accessibility-font-large', 'accessibility-font-xlarge');
    if (s.fontSize === 'large') {
      root.classList.add('accessibility-font-large');
      root.style.fontSize = '17px';
    } else if (s.fontSize === 'xlarge') {
      root.classList.add('accessibility-font-xlarge');
      root.style.fontSize = '19px';
    } else {
      root.style.fontSize = '15px';
    }

    // Large cursor
    if (s.largeCursor) {
      root.classList.add('accessibility-large-cursor');
    } else {
      root.classList.remove('accessibility-large-cursor');
    }
  };

  const increaseFontSize = () =>
    setSettings(prev => ({
      ...prev,
      fontSize: prev.fontSize === 'normal' ? 'large' : prev.fontSize === 'large' ? 'xlarge' : 'xlarge',
    }));

  const decreaseFontSize = () =>
    setSettings(prev => ({
      ...prev,
      fontSize: prev.fontSize === 'xlarge' ? 'large' : prev.fontSize === 'large' ? 'normal' : 'normal',
    }));

  const toggleLargeCursor = () =>
    setSettings(prev => ({ ...prev, largeCursor: !prev.largeCursor }));

  const resetSettings = () => setSettings(DEFAULT_SETTINGS);

  return {
    settings,
    isOpen,
    setIsOpen,
    increaseFontSize,
    decreaseFontSize,
    toggleLargeCursor,
    resetSettings,
  };
}
